import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';
import { getDashboardStats, getCashflowChart, getCashflowScenarios } from '../services/api';
import api from '../services/api';
import { PageTransition } from '../components/motion/Animations';
import { StatCard, SurfaceCard } from '../components/ui/filters-toolkit';
import { 
  TrendingUp, 
  Clock, 
  AlertTriangle, 
  Users, 
  FileText, 
  FilePlus,
  ArrowRight,
  TrendingDown,
  Scale,
  Bell,
  CalendarDays,
  MapPin,
  Briefcase,
  Wallet,
  Activity,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  AlertCircle,
  X,
  Loader2,
  PiggyBank,
  Check,
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Area,
  AreaChart,
  Legend,
  ReferenceLine,
} from 'recharts';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    useGrouping: 'always', style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

const DeltaPill = ({ delta, invert = false, testid }) => {
  if (!delta || delta.kind === 'flat') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-extrabold bg-white/25 backdrop-blur-sm text-white px-2 py-0.5 rounded-full" data-testid={testid}>
        <Minus className="w-3 h-3" /> Estable
      </span>
    );
  }
  if (delta.kind === 'new') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-extrabold bg-white/25 backdrop-blur-sm text-white px-2 py-0.5 rounded-full" data-testid={testid}>
        Nuevo
      </span>
    );
  }
  // invert=true → "down" is the desirable direction (e.g. expenses dropping).
  const good = invert ? delta.kind === 'down' : delta.kind === 'up';
  const Icon = delta.kind === 'up' ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-extrabold backdrop-blur-sm px-2 py-0.5 rounded-full ${good ? 'bg-white/30 text-white' : 'bg-black/20 text-white'}`}
      data-testid={testid}
      title={`vs ${delta.prev_label || 'periodo anterior'}`}
    >
      <Icon className="w-3 h-3" />
      {delta.pct > 0 ? '+' : ''}{delta.pct}%
    </span>
  );
};


const statusLabels = {
  draft: 'Borrador',
  sent: 'Enviada',
  paid: 'Pagada',
  overdue: 'Vencida',
  cancelled: 'Cancelada'
};

const StatusBadge = ({ status }) => {
  return (
    <span className={`status-badge status-${status}`}>
      {statusLabels[status] || status}
    </span>
  );
};

export const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taxUpcoming, setTaxUpcoming] = useState([]);
  const [cashflow, setCashflow] = useState([]);
  const [cashflowAlerts, setCashflowAlerts] = useState([]);
  const [cfHorizon, setCfHorizon] = useState(6);
  const [cfScenarios, setCfScenarios] = useState([]);
  const [cfScenarioId, setCfScenarioId] = useState(null); // null = main/base
  const [cfLoading, setCfLoading] = useState(false);
  const [heroPeriod, setHeroPeriod] = useState(() => {
    try { return localStorage.getItem('dashboard:heroPeriod') || 'year'; } catch { return 'year'; }
  });
  const [heroLoading, setHeroLoading] = useState(false);

  const [taxAlerts, setTaxAlerts] = useState([]);
  const [taxAlertsDismissed, setTaxAlertsDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dashboard:taxAlertsDismissed') || '[]'); } catch { return []; }
  });
  const [taxReserves, setTaxReserves] = useState([]);  // reserve_ids ya creadas
  const [reservingKey, setReservingKey] = useState(null);  // reserve_id en proceso

  useEffect(() => {
    loadStats(heroPeriod);
    api.get('/tax-forecast/current')
      .then((r) => setTaxUpcoming((r.data?.upcoming || []).slice(0, 3)))
      .catch(() => {});
    api.get('/tax-forecast/alerts')
      .then((r) => setTaxAlerts(r.data?.alerts || []))
      .catch(() => {});
    api.get('/tax-forecast/reserves')
      .then((r) => setTaxReserves((r.data?.reserves || []).map((x) => x.reserve_id)))
      .catch(() => {});
    getCashflowScenarios()
      .then((r) => setCfScenarios((r.data || []).filter((s) => !s.is_main)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reserveTaxAmount = async (alert) => {
    const reserveId = `tax_reserve_${alert.type}_${(alert.message.split('·')[1] || '').trim().replace(/ /g, '_')}`;
    setReservingKey(reserveId);
    try {
      const periodLabel = (alert.message.split('·')[1] || '').trim();
      const { data } = await api.post('/tax-forecast/reserve', {
        tax_type: alert.type,
        period_label: periodLabel,
        amount: alert.amount,
        due_date: alert.due_date,
      });
      setTaxReserves((prev) => [...prev.filter((x) => x !== data.reserve_id), data.reserve_id]);
      toast.success(`Tesorería apartada · ${alert.amount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })} verás un gasto proyectado el ${new Date(alert.due_date).toLocaleDateString('es-ES')}`);
    } catch (_e) {
      toast.error('No se pudo apartar la tesorería');
    } finally {
      setReservingKey(null);
    }
  };

  const releaseTaxReserve = async (alert) => {
    const periodLabel = (alert.message.split('·')[1] || '').trim();
    const reserveId = `tax_reserve_${alert.type}_${periodLabel.replace(/ /g, '_')}`;
    setReservingKey(reserveId);
    try {
      await api.delete(`/tax-forecast/reserve/${reserveId}`);
      setTaxReserves((prev) => prev.filter((x) => x !== reserveId));
      toast.success('Reserva eliminada');
    } catch (_e) {
      toast.error('No se pudo eliminar la reserva');
    } finally {
      setReservingKey(null);
    }
  };

  const dismissTaxAlert = (alertKey) => {
    const next = [...taxAlertsDismissed, alertKey];
    setTaxAlertsDismissed(next);
    try { localStorage.setItem('dashboard:taxAlertsDismissed', JSON.stringify(next)); } catch {}
  };
  const visibleTaxAlerts = taxAlerts.filter((a) => !taxAlertsDismissed.includes(`${a.type}-${a.due_date}`));

  // Refetch stats when period changes
  useEffect(() => {
    if (loading) return; // first load is handled by the mount effect
    setHeroLoading(true);
    loadStats(heroPeriod).finally(() => setHeroLoading(false));
    try { localStorage.setItem('dashboard:heroPeriod', heroPeriod); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroPeriod]);

  // Refetch cashflow whenever horizon or scenario changes
  useEffect(() => {
    setCfLoading(true);
    const params = { months: cfHorizon };
    if (cfScenarioId) params.scenario_id = cfScenarioId;
    getCashflowChart(params)
      .then((r) => {
        setCashflow(r.data?.data || []);
        setCashflowAlerts(r.data?.alerts || []);
      })
      .catch(() => {})
      .finally(() => setCfLoading(false));
  }, [cfHorizon, cfScenarioId]);

  const loadStats = async (period) => {
    try {
      const { data } = await getDashboardStats(period ? { period } : {});
      setStats(data);
    } catch (e) {
      console.error('Error loading stats:', e);
    } finally {
      setLoading(false);
    }
  };

  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  const chartData = stats?.monthly_revenue 
    ? Object.entries(stats.monthly_revenue).map(([month, value]) => ({
        name: monthNames[parseInt(month) - 1],
        revenue: value
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <PageTransition>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-stone-950">Dashboard</h1>
            <p className="text-stone-400 text-sm mt-1">Resumen de tu facturación</p>
          </div>
          <PageHelpButton moduleKey="dashboard" variant="icon" />
        </div>
        <Link
          to="/invoices/new"
          className="inline-flex items-center gap-2 bg-[#FF6B4A] text-white px-6 py-2.5 rounded-full hover:bg-[#E05A3E] transition-all duration-300 font-semibold text-sm w-full sm:w-auto justify-center hover:-translate-y-0.5 shadow-lg shadow-orange-200/50"
          data-testid="create-invoice-button"
        >
          <FilePlus className="w-4 h-4" />
          Nueva Factura
        </Link>
      </div>

      {/* Overdue invoices + Profitability alerts removed — now centralized in NotificationCenter (sidebar bell). */}

      {/* Smart Tax Alerts — pre-vencimiento (≤14d) con sugerencias de tesorería */}
      {visibleTaxAlerts.length > 0 && (
        <div className="space-y-2" data-testid="tax-alerts-stack">
          {visibleTaxAlerts.map((a) => {
            const alertKey = `${a.type}-${a.due_date}`;
            const styles = a.level === 'urgent'
              ? 'from-rose-50 to-red-50 border-rose-200 text-rose-900'
              : a.level === 'warning'
                ? 'from-amber-50 to-orange-50 border-amber-200 text-amber-900'
                : 'from-violet-50 to-fuchsia-50 border-violet-200 text-violet-900';
            const accent = a.level === 'urgent' ? 'text-rose-600' : a.level === 'warning' ? 'text-amber-600' : 'text-violet-600';
            const periodLabel = (a.message.split('·')[1] || '').trim();
            const reserveId = `tax_reserve_${a.type}_${periodLabel.replace(/ /g, '_')}`;
            const isReserved = taxReserves.includes(reserveId);
            const isBusy = reservingKey === reserveId;
            return (
              <div
                key={alertKey}
                className={`relative bg-gradient-to-r ${styles} border rounded-2xl p-4 pr-12 flex items-start gap-3`}
                data-testid={`tax-alert-${a.type}`}
              >
                <div className={`w-9 h-9 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0 ${accent}`}>
                  <AlertCircle className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{a.title}</p>
                  <p className="text-xs opacity-80 mt-0.5">{a.message}</p>
                  {a.suggestion && (
                    <p className="text-xs mt-2 leading-relaxed">{a.suggestion}</p>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    {!isReserved ? (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => reserveTaxAmount(a)}
                        className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/80 hover:bg-white border ${a.level === 'urgent' ? 'border-rose-200 text-rose-700' : a.level === 'warning' ? 'border-amber-200 text-amber-700' : 'border-violet-200 text-violet-700'} transition disabled:opacity-50`}
                        data-testid={`tax-alert-reserve-${a.type}`}
                      >
                        {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PiggyBank className="w-3 h-3" />}
                        Apartar tesorería
                      </button>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                          <Check className="w-3 h-3" /> Tesorería apartada
                        </span>
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => releaseTaxReserve(a)}
                          className="text-[11px] text-stone-600 hover:text-stone-900 hover:underline disabled:opacity-50"
                          data-testid={`tax-alert-release-${a.type}`}
                        >
                          Liberar
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => dismissTaxAlert(alertKey)}
                  className="absolute top-3 right-3 w-6 h-6 rounded-full hover:bg-white/50 flex items-center justify-center opacity-60 hover:opacity-100 transition"
                  data-testid={`tax-alert-dismiss-${a.type}`}
                  aria-label="Descartar"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Próximos vencimientos fiscales */}
      {taxUpcoming.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="tax-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-stone-800 inline-flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-[#FF6B4A]" /> Próximos impuestos
            </h3>
            <Link to="/tax/forecast" className="text-xs text-[#FF6B4A] font-medium hover:underline">Ver predicción →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {taxUpcoming.map((t) => (
              <Link
                key={`${t.type}-${t.period_year}-${t.period_quarter}`}
                to="/tax/forecast"
                className="bg-stone-50 hover:bg-stone-100 rounded-xl p-3 border border-stone-100 transition"
                data-testid={`tax-widget-${t.type}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold uppercase text-stone-500">Modelo {t.type}</span>
                  <span className="text-[10px] text-stone-400">{t.days_to_due}d</span>
                </div>
                <p className="text-xs text-stone-600 mb-1">{t.label}</p>
                <p className={`text-lg font-bold ${t.indicator === 'compensar' ? 'text-emerald-600' : 'text-stone-900'}`}>
                  {t.indicator === 'compensar' ? '-' : ''}{formatCurrency(t.projected_at_period_end)}
                </p>
                <p className="text-[10px] text-stone-500">Vence {new Date(t.due_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* HERO · Ingresos · Gastos · Balance Neto */}
      <div className="flex items-center justify-between flex-wrap gap-3" data-testid="hero-period-toggle">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-stone-600">Resumen financiero</h2>
          <span className="text-xs text-stone-400 hidden sm:inline">· {stats?.period_label || ''}</span>
          {heroLoading && <span className="text-[10px] text-stone-400 ml-2">Actualizando…</span>}
        </div>
        <div className="inline-flex items-center gap-1 bg-stone-100 p-1 rounded-full">
          <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500 px-2 hidden sm:inline">Periodo</span>
          {[
            { v: 'month', l: 'Mes' },
            { v: 'quarter', l: 'Trimestre' },
            { v: 'year', l: 'Año' },
          ].map((p) => (
            <button
              key={p.v}
              type="button"
              onClick={() => setHeroPeriod(p.v)}
              className={`text-xs font-bold px-3.5 py-1.5 rounded-full transition-all ${heroPeriod === p.v ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
              data-testid={`hero-period-${p.v}`}
            >
              {p.l}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 sm:gap-4 -mt-3" data-testid="hero-financial-block">
        <div className="relative overflow-hidden rounded-3xl p-4 sm:p-6 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 shadow-[0_20px_50px_-12px_rgb(16,185,129,0.45)] hover:shadow-[0_24px_60px_-12px_rgb(16,185,129,0.55)] transition-all hover:-translate-y-0.5" data-testid="income-card">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner">
                <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-white/90">Ingresos</span>
              <span className="ml-auto"><DeltaPill delta={stats?.revenue_delta} testid="hero-revenue-delta" /></span>
            </div>
            <p className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-white font-heading tracking-tight drop-shadow-sm">
              {formatCurrency(stats?.total_revenue || 0)}
            </p>
            <p className="text-[10px] sm:text-xs font-semibold text-white/80 mt-1.5 sm:mt-2">
              {stats?.period_label || ''}{stats?.prev_period_label ? ` · vs ${stats.prev_period_label}` : ''}
            </p>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-3xl p-4 sm:p-6 bg-gradient-to-br from-rose-500 via-red-500 to-orange-500 shadow-[0_20px_50px_-12px_rgb(244,63,94,0.45)] hover:shadow-[0_24px_60px_-12px_rgb(244,63,94,0.55)] transition-all hover:-translate-y-0.5" data-testid="expenses-dashboard-card">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner">
                <TrendingDown className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-white/90">Gastos</span>
              <span className="ml-auto"><DeltaPill delta={stats?.expenses_delta} invert testid="hero-expenses-delta" /></span>
            </div>
            <p className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-white font-heading tracking-tight drop-shadow-sm">
              −{formatCurrency(stats?.total_expenses || 0)}
            </p>
            <p className="text-[10px] sm:text-xs font-semibold text-white/80 mt-1.5 sm:mt-2">
              {stats?.period_label || ''}{stats?.prev_period_label ? ` · vs ${stats.prev_period_label}` : ''}
            </p>
          </div>
        </div>

        {(() => {
          const net = stats?.net_balance || 0;
          const positive = net >= 0;
          const gradient = positive
            ? 'bg-gradient-to-br from-[#FF6B4A] via-[#F97316] to-[#FBBF24]'
            : 'bg-gradient-to-br from-red-600 via-red-700 to-rose-800';
          const shadow = positive
            ? 'shadow-[0_20px_50px_-12px_rgb(255,107,74,0.55)] hover:shadow-[0_28px_70px_-12px_rgb(255,107,74,0.65)]'
            : 'shadow-[0_20px_50px_-12px_rgb(220,38,38,0.5)] hover:shadow-[0_28px_70px_-12px_rgb(220,38,38,0.6)]';
          const margin = (stats?.total_revenue || 0) > 0
            ? Math.round((net / stats.total_revenue) * 100)
            : null;
          return (
            <div className={`relative overflow-hidden rounded-3xl p-4 sm:p-6 ${gradient} ${shadow} transition-all hover:-translate-y-0.5`} data-testid="net-balance-card">
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/15 blur-2xl" />
              <div className="absolute -bottom-12 -left-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2.5 mb-3 flex-wrap">
                  <div className="w-10 h-10 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center shadow-inner">
                    <Scale className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-white/95">Balance Neto</span>
                  <span className="ml-auto flex items-center gap-1.5">
                    {margin !== null && (
                      <span className="text-[10px] sm:text-[11px] font-extrabold bg-white/25 backdrop-blur-sm text-white px-2 py-0.5 rounded-full">
                        Margen {margin}%
                      </span>
                    )}
                    <DeltaPill delta={stats?.net_delta} testid="hero-net-delta" />
                  </span>
                </div>
                <p className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-white font-heading tracking-tight drop-shadow-md">
                  {formatCurrency(net)}
                </p>
                <p className="text-[10px] sm:text-xs font-semibold text-white/85 mt-1.5 sm:mt-2">
                  {positive ? 'Beneficio del periodo' : 'Pérdida · revisa gastos'}
                </p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Stats Grid - colorful gradient cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="Facturado"
          value={formatCurrency(stats?.total_revenue || 0)}
          sub={`${stats?.current_year || ''}`}
          icon={TrendingUp}
          gradient="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50"
          iconBg="bg-gradient-to-br from-emerald-500 to-teal-500"
          testid="total-revenue-card"
        />
        <StatCard
          label="Pendiente"
          value={formatCurrency(stats?.pending_amount || 0)}
          sub="Por cobrar"
          icon={Clock}
          gradient="bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50"
          iconBg="bg-gradient-to-br from-sky-500 to-indigo-500"
          testid="pending-amount-card"
        />
        <StatCard
          label="Vencido"
          value={formatCurrency(stats?.overdue_amount || 0)}
          sub={stats?.overdue_invoices?.length ? `${stats.overdue_invoices.length} factura${stats.overdue_invoices.length === 1 ? '' : 's'}` : '—'}
          icon={AlertTriangle}
          gradient="bg-gradient-to-br from-rose-50 via-red-50 to-orange-50"
          iconBg="bg-gradient-to-br from-rose-500 to-red-500"
          testid="overdue-amount-card"
        />
        <StatCard
          label="Clientes"
          value={stats?.clients_count || 0}
          sub="Activos"
          icon={Users}
          gradient="bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50"
          iconBg="bg-gradient-to-br from-violet-500 to-purple-500"
          testid="clients-count-card"
        />
      </div>

      {/* (Income/Expenses/Net block moved to HERO above) */}

      {/* Cashflow Widget - Composed Chart with bars + line */}
      <SurfaceCard testid="cashflow-widget">
          <div className="p-6 pb-2 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-200/50">
                <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="text-lg font-heading font-extrabold tracking-tight">Cashflow · Próximos {cfHorizon} meses</h3>
                <p className="text-xs text-stone-500 mt-0.5">Ingresos vs Gastos · Saldo proyectado{cfScenarioId ? ' · Escenario aplicado' : ''}</p>
              </div>
            </div>
            <Link to="/cashflow" className="inline-flex items-center gap-1 text-xs font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 px-3 py-1.5 rounded-full transition-all" data-testid="dashboard-cashflow-link">
              Análisis detallado <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Horizon + Scenario toggles */}
          <div className="px-6 pb-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 bg-stone-100 p-1 rounded-full" data-testid="cashflow-horizon-toggle">
              <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500 px-2">Horizonte</span>
              {[3, 6, 12].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setCfHorizon(m)}
                  className={`text-xs font-bold px-3 py-1 rounded-full transition-all ${cfHorizon === m ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                  data-testid={`cf-horizon-${m}m`}
                >
                  {m}m
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1.5 bg-stone-100 p-1 rounded-full flex-wrap" data-testid="cashflow-scenario-toggle">
              <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500 px-2">Escenario</span>
              <button
                type="button"
                onClick={() => setCfScenarioId(null)}
                className={`text-xs font-bold px-3 py-1 rounded-full transition-all ${!cfScenarioId ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                data-testid="cf-scenario-base"
              >
                Base
              </button>
              {cfScenarios.length === 0 ? (
                <Link to="/cashflow" className="text-[10px] font-semibold text-violet-600 hover:underline px-2" data-testid="cf-scenarios-empty">
                  + Crear escenario
                </Link>
              ) : (
                cfScenarios.slice(0, 4).map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setCfScenarioId(s.id)}
                    className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full transition-all ${cfScenarioId === s.id ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                    data-testid={`cf-scenario-${s.id}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: s.color || '#A78BFA' }} />
                    {s.name}
                  </button>
                ))
              )}
            </div>

            {cfLoading && <span className="text-[10px] text-stone-400">Actualizando…</span>}
          </div>

          {cashflowAlerts.length > 0 && (
            <div className="px-6 pb-3 flex flex-wrap gap-2">
              {cashflowAlerts.slice(0, 3).map((a, i) => (
                <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${a.severity === 'danger' || a.type === 'negative_balance' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                  <AlertTriangle className="w-3 h-3" /> {a.message || a.title}
                </span>
              ))}
            </div>
          )}

          {cashflow.length === 0 ? (
            <div className="px-6 pb-6 py-10 text-center text-sm text-stone-400">
              <Activity className="w-8 h-8 mx-auto mb-2 text-stone-300" />
              {cfLoading ? 'Cargando proyección…' : 'Sin datos para este horizonte/escenario'}
            </div>
          ) : (
          <div className="px-2 sm:px-6 pb-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={cashflow.map(m => ({ ...m, month: (m.month || '').slice(2) }))}>
                <defs>
                  <linearGradient id="cf-income" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0.55} />
                  </linearGradient>
                  <linearGradient id="cf-expense" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#F43F5E" stopOpacity={0.55} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F4" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#78716C' }} axisLine={{ stroke: '#E7E5E4' }} />
                <YAxis
                  tick={{ fontSize: 11, fill: '#78716C' }}
                  axisLine={{ stroke: '#E7E5E4' }}
                  tickFormatter={(v) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', maximumFractionDigits: 0, notation: 'compact' }).format(v || 0) + '€'}
                />
                <Tooltip
                  formatter={(value, name) => [formatCurrency(value), name]}
                  contentStyle={{ background: '#fff', border: '1px solid #E7E5E4', borderRadius: 12, fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                <ReferenceLine y={0} stroke="#A8A29E" strokeDasharray="2 4" />
                <Bar dataKey="ingresos" name="Ingresos" fill="url(#cf-income)" radius={[8, 8, 0, 0]} barSize={20} />
                <Bar dataKey="gastos" name="Gastos" fill="url(#cf-expense)" radius={[8, 8, 0, 0]} barSize={20} />
                <Line
                  type="monotone"
                  dataKey="saldo"
                  name="Saldo proyectado"
                  stroke="#FF6B4A"
                  strokeWidth={3}
                  dot={{ fill: '#FF6B4A', strokeWidth: 2, stroke: '#fff', r: 5 }}
                  activeDot={{ r: 7, fill: '#FF6B4A' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          )}

          {cashflow.length > 0 && (
          <div className="px-6 pb-5 grid grid-cols-3 gap-3">
            {(() => {
              const lastM = cashflow[cashflow.length - 1] || {};
              const minSaldo = Math.min(...cashflow.map(m => m.saldo || 0));
              const totalInc = cashflow.reduce((s, m) => s + (m.ingresos || 0), 0);
              return (
                <>
                  <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-3" data-testid="cashflow-mini-income">
                    <p className="text-[10px] uppercase font-bold tracking-widest text-emerald-700 mb-1">Ingresos {cfHorizon}m</p>
                    <p className="text-base font-extrabold text-emerald-900 font-heading">{formatCurrency(totalInc)}</p>
                  </div>
                  <div className={`rounded-xl p-3 border ${minSaldo < 0 ? 'bg-red-50/60 border-red-100' : 'bg-amber-50/60 border-amber-100'}`} data-testid="cashflow-mini-min">
                    <p className={`text-[10px] uppercase font-bold tracking-widest mb-1 ${minSaldo < 0 ? 'text-red-700' : 'text-amber-700'}`}>Saldo mínimo</p>
                    <p className={`text-base font-extrabold font-heading ${minSaldo < 0 ? 'text-red-900' : 'text-amber-900'}`}>{formatCurrency(minSaldo)}</p>
                  </div>
                  <div className="bg-violet-50/60 border border-violet-100 rounded-xl p-3" data-testid="cashflow-mini-final">
                    <p className="text-[10px] uppercase font-bold tracking-widest text-violet-700 mb-1">Saldo final</p>
                    <p className={`text-base font-extrabold font-heading ${(lastM.saldo || 0) >= 0 ? 'text-violet-900' : 'text-red-900'}`}>{formatCurrency(lastM.saldo || 0)}</p>
                  </div>
                </>
              );
            })()}
          </div>
          )}
        </SurfaceCard>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart - colorful area */}
        <div className="lg:col-span-2 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-6 animate-fade-in-up stagger-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] flex items-center justify-center shadow-lg shadow-orange-200/50">
                <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="text-lg font-heading font-extrabold tracking-tight">Facturación {stats?.current_year}</h3>
                <p className="text-xs text-stone-500">Evolución mensual</p>
              </div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="rev-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6B4A" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#FF6B4A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F4" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#78716C' }}
                  axisLine={{ stroke: '#E7E5E4' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#78716C' }}
                  axisLine={{ stroke: '#E7E5E4' }}
                  tickFormatter={(value) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', maximumFractionDigits: 0, notation: 'compact' }).format(value || 0) + '€'}
                />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Facturado']}
                  contentStyle={{ 
                    backgroundColor: '#fff',
                    border: '1px solid #E7E5E4',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#FF6B4A"
                  strokeWidth={3}
                  fill="url(#rev-area)"
                  dot={{ fill: '#FF6B4A', strokeWidth: 2, stroke: '#fff', r: 4 }}
                  activeDot={{ r: 7, fill: '#FF6B4A' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quarterly Summary - colorful bars */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-6 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-lg shadow-violet-200/50">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="text-lg font-heading font-extrabold tracking-tight">Por Trimestre</h3>
              <p className="text-xs text-stone-500">{stats?.current_year}</p>
            </div>
          </div>
          <div className="space-y-3">
            {(() => {
              const entries = stats?.quarterly_totals ? Object.entries(stats.quarterly_totals) : [];
              const maxV = Math.max(1, ...entries.map(([, v]) => v || 0));
              const grads = [
                'from-emerald-400 to-teal-500',
                'from-sky-400 to-blue-500',
                'from-violet-400 to-purple-500',
                'from-amber-400 to-orange-500',
              ];
              return entries.map(([quarter, amount], i) => (
                <div key={quarter} className="space-y-1.5">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-bold text-stone-700">{quarter}</span>
                    <span className="font-mono font-semibold text-stone-900">{formatCurrency(amount)}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-stone-100 overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${grads[i % grads.length]} rounded-full transition-all duration-700`}
                      style={{ width: `${Math.max(4, (amount / maxV) * 100)}%` }}
                    />
                  </div>
                </div>
              ));
            })()}
          </div>
          <Link 
            to="/history" 
            className="mt-5 inline-flex items-center gap-1 text-sm text-[#FF6B4A] font-bold hover:underline"
            data-testid="view-history-link"
          >
            Ver historial completo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Upcoming Calendar Events */}
      {stats?.upcoming_events?.length > 0 && (
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 animate-fade-in-up" data-testid="upcoming-events-widget">
          <div className="px-6 py-4 border-b border-stone-100 flex justify-between items-center">
            <h3 className="text-lg font-heading font-bold flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#FF6B4A]" />
              Próximos Eventos (7 días)
            </h3>
            <Link to="/calendar" className="text-sm text-[#FF6B4A] font-medium hover:underline inline-flex items-center gap-1" data-testid="view-calendar-link">
              Ver calendario <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-zinc-100">
            {stats.upcoming_events.map(ev => {
              const catColors = { inspeccion_trabajo: 'bg-red-500', juicio: 'bg-purple-500', reunion: 'bg-blue-500', vencimiento_fiscal: 'bg-amber-500', smac: 'bg-orange-500', otro: 'bg-stone-500' };
              const catLabels = { inspeccion_trabajo: 'Inspección', juicio: 'Juicio', reunion: 'Reunión', vencimiento_fiscal: 'Venc. Fiscal', smac: 'SMAC', otro: 'Otro' };
              return (
                <div key={ev.id} className="px-6 py-3 flex items-center gap-4 hover:bg-orange-50" data-testid={`upcoming-event-${ev.id}`}>
                  <div className="text-center flex-shrink-0 w-12">
                    <div className="text-xs text-stone-400">{new Date(ev.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short' })}</div>
                    <div className="text-lg font-bold text-stone-800">{ev.date.split('-')[2]}</div>
                  </div>
                  <span className={`w-1 h-10 rounded-full ${catColors[ev.category] || 'bg-stone-400'} flex-shrink-0`}></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{ev.title}</p>
                    <div className="flex items-center gap-3 text-xs text-stone-500">
                      <span className={`px-1.5 py-0.5 rounded text-white text-[10px] font-medium ${catColors[ev.category] || 'bg-stone-400'}`}>{catLabels[ev.category] || 'Otro'}</span>
                      {ev.time && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{ev.time}</span>}
                      {ev.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{ev.location}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Invoices */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 animate-fade-in-up">
        <div className="px-6 py-4 border-b border-stone-100 flex justify-between items-center">
          <h3 className="text-lg font-heading font-bold">Facturas Recientes</h3>
          <Link 
            to="/invoices" 
            className="text-sm text-[#FF6B4A] font-medium hover:underline inline-flex items-center gap-1"
            data-testid="view-all-invoices-link"
          >
            Ver todas
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full invoice-table">
            <thead>
              <tr>
                <th className="text-left px-6 py-3">Número</th>
                <th className="text-left px-6 py-3">Cliente</th>
                <th className="text-left px-6 py-3">Fecha</th>
                <th className="text-left px-6 py-3">Estado</th>
                <th className="text-right px-6 py-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recent_invoices?.length > 0 ? (
                stats.recent_invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-orange-50">
                    <td className="px-6 py-4">
                      <Link 
                        to={`/invoices/${invoice.id}`} 
                        className="font-mono text-sm text-[#FF6B4A] hover:underline"
                        data-testid={`invoice-link-${invoice.id}`}
                      >
                        {invoice.invoice_number}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm">{invoice.client_name}</td>
                    <td className="px-6 py-4 text-sm text-stone-500">{invoice.issue_date}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={invoice.status} />
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-sm font-medium">
                      {formatCurrency(invoice.total)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-stone-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                    <p>No hay facturas todavía</p>
                    <Link 
                      to="/invoices/new" 
                      className="text-[#FF6B4A] font-medium hover:underline mt-1 inline-block"
                      data-testid="create-first-invoice-link"
                    >
                      Crear primera factura
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </PageTransition>
  );
};

export default DashboardPage;
